/**
 * @description
 * Promise-based transaction creation relying on IDBTransaction events.
 */
export const create =
  (
    db: IDBDatabase,
    storeName: string,
    mode: IDBTransactionMode,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    op: (store: IDBObjectStore, ...args: any[]) => IDBRequest | IDBRequest[],
  ) =>
  <T, U>(...args: T[]) =>
    new Promise<IDBRequest<U> | IDBRequest<U>[]>((resolve, reject) => {
      const transaction = db.transaction(storeName, mode);
      const store = transaction.objectStore(storeName);
      const req = op(store, ...args);
      /**
       * First iteration will try to not handle request events, see below.
       *
       * @note
       * From the IDBTransaction error spec:
       *
       * > If this transaction was aborted due to a failed request,
       * > this will be the same as the request's error.
       * > If this transaction was aborted due to an uncaught exception in an event handler,
       * > the error will be a "AbortError" DOMException.
       * > If the transaction was aborted due to an error while committing,
       * > it will reflect the reason for the failure
       * > (e.g. "QuotaExceededError", "ConstraintError", or "UnknownError" DOMException).
       *
       * @link {https://w3c.github.io/IndexedDB/#dom-idbtransaction-error}
       */
      transaction.oncomplete = () => resolve(req);
      transaction.onerror = () => reject(transaction.error);
    });
